<template>
  <div class="academic-pagination">
    <div class="pager-navigation">
      <button :disabled="current === 1" @click="emitPage(current - 1)" class="nav-arrow prev">
        PREV
      </button>

      <div class="page-list">
        <template v-for="(page, index) in visiblePages" :key="index">
          <button v-if="typeof page === 'number'" :class="['page-item', { 'is-active': page === current }]"
            @click="emitPage(page)">
            {{ String(page).padStart(2, '0') }}
          </button>
          <span v-else class="page-sep">···</span>
        </template>
      </div>

      <button :disabled="current === totalPages" @click="emitPage(current + 1)" class="nav-arrow next">
        NEXT
      </button>
    </div>

    <div class="pager-meta">
      <span class="meta-label">PAGE</span>
      <span class="meta-value">{{ current }} / {{ totalPages }}</span>
      <span class="total-count">TOTAL {{ total }}</span>
    </div>
  </div>
</template>

<script setup>
  import { computed } from 'vue';

  const props = defineProps({
    current: { type: Number, default: 1 },
    total: { type: Number, default: 0 }, // 数据总条数
    pageSize: { type: Number, default: 10 }
  });

  const emit = defineEmits(['change']);

  const totalPages = computed(() => Math.max(1, Math.ceil(props.total / props.pageSize)));

  const visiblePages = computed(() => {
    const tp = totalPages.value;
    const cur = props.current;
    const pages = [];

    if (tp <= 5) {
      for (let i = 1; i <= tp; i++) pages.push(i);
    } else {
      pages.push(1);
      if (cur > 3) pages.push('sep');

      const start = Math.max(2, cur - 1);
      const end = Math.min(tp - 1, cur + 1);

      // 确保中间始终显示 3 个左右
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) pages.push(i);
      }

      if (cur < tp - 2) pages.push('sep');
      if (!pages.includes(tp)) pages.push(tp);
    }
    return pages;
  });

  const emitPage = (page) => {
    if (page >= 1 && page <= totalPages.value && page !== props.current) {
      emit('change', page);
    }
  };
</script>

<style scoped>
  .academic-pagination {
    width: 100%;
    padding: 40px 0 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }

  /* 1. 元数据风格：呼应卡片顶部的 #ID 部分 */
  .pager-meta {
    display: flex;
    align-items: center;
    gap: 12px;
    font-family: 'SF Mono', Monaco, monospace;
    font-size: 11px;
    letter-spacing: 1px;
  }

  .meta-label {
    color: #007AFF;
    font-weight: 700;
  }

  .meta-value {
    color: #1d1d1f;
    background: #f5f5f7;
    padding: 2px 8px;
    border-radius: 4px;
  }

  .total-count {
    color: #86868b;
  }

  /* 2. 导航区域 */
  .pager-navigation {
    display: flex;
    align-items: center;
    gap: 24px;
  }

  .page-list {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  /* 3. 页码按钮：取消背景和边框，使用下划线指示 */
  .page-item {
    min-width: 32px;
    height: 32px;
    background: transparent;
    border: none;
    font-size: 14px;
    color: #86868b;
    cursor: pointer;
    position: relative;
    transition: all 0.2s;
    font-family: 'SF Mono', sans-serif;
  }

  .page-item:hover {
    color: #1d1d1f;
  }

  .page-item.is-active {
    color: #007AFF;
    font-weight: 700;
  }

  .page-item.is-active::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 20%;
    width: 60%;
    height: 2px;
    background: #007AFF;
    border-radius: 2px;
  }

  /* 4. 左右箭头改用文字描述 */
  .nav-arrow {
    background: transparent;
    border: none;
    font-size: 11px;
    font-weight: 700;
    color: #1d1d1f;
    cursor: pointer;
    letter-spacing: 1px;
    padding: 8px 0;
    transition: opacity 0.2s;
  }

  .nav-arrow:hover:not(:disabled) {
    color: #007AFF;
  }

  .nav-arrow:disabled {
    color: #d2d2d7;
    cursor: not-allowed;
  }

  .page-sep {
    color: #d2d2d7;
    font-size: 12px;
    margin: 0 4px;
  }
</style>